<template>
    <div class="app--field-password">
        <div class="input-group">
            <slot name="prepend"></slot>
            <input
                ref="input"
                v-validate="validate"
                :type="type"
                class="form-control"
                autocomplete="off"
                :class="cssClasses"
                :value="value"
                :autofocus="autofocus"
                :placeholder="placeholder"
                :required="validateRequired"
                :minlength="validateMinlength"
                :maxlength="validateMaxlength"
                @input="input"
                @keyup.enter="$emit('keyenter')"
                @keyup="isCapsOn"
                @focus="toggle(true)"
                @blur="toggle(false)"
            />
            <slot name="append">
                <div
                    v-if="visibilityToggle"
                    class="visibility-toggle text-muted cursor-pointer"
                    @click="type = type == 'password' ? 'text' : 'password'"
                >
                    <AppIcon
                        :key="type == 'password' ? 'eye' : 'eye-off'"
                        class="fa-fw"
                        :value="type == 'password' ? 'eye' : 'eye-off'"
                    ></AppIcon>
                </div>
            </slot>
        </div>
        <div class="invalid-feedback"></div>
        <div
            v-if="validateStrength || validateCaps || rules.length > 0"
            v-show="
                (validateStrength && visible && info != null) || (validateCaps && capsOn && visible) || rules.length > 0
            "
            ref="popover"
            class="card popper"
            role="tooltip"
            width="200"
        >
            <div class="p-content">
                <div class="p-2 text-center">
                    <p v-if="info && strength < 2" class="text-danger m-0">{{ $t('validation.strength.invalid') }}</p>
                    <p v-if="info && strength == 2" class="text-warning m-0">{{ $t('validation.strength.weak') }}</p>
                    <p v-if="info && strength > 2" class="text-success m-0">{{ $t('validation.strength.strong') }}</p>
                    <p v-if="!info && ((validateCaps && capsOn) || rules.length > 0)" class="text-warning m-0">
                        Warning
                    </p>
                </div>
                <ul
                    v-if="suggestions.length > 0 || (validateCaps && capsOn) || rules.length > 0"
                    class="list-group list-group-flush text-muted"
                >
                    <li v-if="validateCaps && capsOn" class="list-group-item p-2 small">Caps Lock is on.</li>
                    <li v-for="suggestion in suggestions" :key="suggestion.id" class="list-group-item p-2 small">{{ suggestion }}</li>
                    <li v-for="rule in rules" :key="rule.id" class="list-group-item p-1 small">{{ rule }}</li>
                </ul>
            </div>
            <div class="p-arrow" data-popper-arrow></div>
        </div>
    </div>
</template>

<script>
    import { computePosition, offset, flip, autoUpdate } from '@floating-ui/dom'

    export default {
        props: {
            value: {
                type: String,
                default: ''
            },
            placeholder: {
                type: String,
                default: ''
            },
            autofocus: {
                type: Boolean,
                default: false
            },
            validateRequired: {
                type: Boolean,
                default: true
            },
            validateMinlength: {
                type: Number,
                default: 8
            },
            validateMaxlength: {
                type: Number,
                default: 32
            },
            validateStrength: {
                type: Boolean,
                default: true
            },
            validateUppercase: {
                type: Boolean,
                default: false
            },
            validateLowercase: {
                type: Boolean,
                default: false
            },
            validateNumber: {
                type: Boolean,
                default: false
            },
            validateSymbol: {
                type: Boolean,
                default: false
            },
            validateCaps: {
                type: Boolean,
                default: true
            },
            validateConfirm: {
                type: String,
                default: null
            },
            visibilityToggle: {
                type: Boolean,
                default: true
            },
            classes: {
                type: String,
                default: ''
            },
            passwordType: {
                type: String,
                default: 'password'
            }
        },
        emits: ['update:value', 'keyenter'],
        data() {
            return {
                loaded: true,
                info: null,
                rules: [],
                popover: null,
                visible: false,
                capsOn: false,
                type: this.passwordType
            }
        },
        computed: {
            strength() {
                return this.info && Object.hasOwn(this.info, 'score') ? this.info.score : 0
            },
            suggestions() {
                return this.info && Object.hasOwn(this.info, 'feedback') ? this.info.feedback.suggestions : []
            },
            cssClasses() {
                return this.classes + (this.visibilityToggle ? ' rounded-right with-toggle' : '')
            }
        },
        created() {
            if (!document.getElementById('zxcvbnjs')) {
                let script = document.createElement('script')
                script.setAttribute('id', 'zxcvbnjs')
                script.async = true
                script.onload = () => {
                    this.loaded = true
                }
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/zxcvbn/4.4.2/zxcvbn.js'
                document.head.appendChild(script)
            }
        },
        mounted() {
            this.$nextTick(function () {
                if (this.autofocus == true) {
                    this.$refs.input.focus()
                }
                if (Object.hasOwn(window, 'zxcvbn')) {
                    this.loaded = true
                }
            })

            if (
                this.validateStrength == true ||
                this.validateCaps ||
                this.validateLowercase ||
                this.validateUppercase ||
                this.validateNumber ||
                this.validateSymbol
            ) {
                this.popover = autoUpdate(this.$refs.input, this.$refs.popover, () => {
                    if (this.$refs.input) {
                        computePosition(this.$refs.input, this.$refs.popover, {
                            placement: 'right',
                            middleware: [offset(8), flip({ fallbackPlacements: ['right', 'top', 'bottom'] })]
                        }).then(({ x, y, placement, middlewareData }) => {
                            Object.assign(this.$refs.popover.style, {
                                left: `${x}px`,
                                top: `${y}px`,
                                position: 'absolute'
                            })
                        })
                    }
                })
            }
        },
        methods: {
            input(event) {
                this.$validate(event)
                this.$emit('update:value', event.target.value)
            },
            validate(event) {
                if (this.validateStrength == true && this.loaded && typeof zxcvbn != 'undefined') {
                    this.info = zxcvbn(event.target.value)
                    event.target.setCustomValidity(
                        this.strength > 1 === false ? this.$t('validation.regex', { attribute: this.placeholder }) : ''
                    )
                }
                let validateRules =
                    this.validateUppercase || this.validateLowercase || this.validateNumber || this.validateSymbol
                let v = event.target.value
                this.rules = []
                if (this.validateUppercase && /[A-Z]/.test(v) == false) {
                    this.rules.push('This field must contain at least one uppercase character.')
                }
                if (this.validateLowercase && /[a-z]/.test(v) == false) {
                    this.rules.push('This field must contain at least one lowercase character.')
                }
                if (this.validateNumber && /[0-9]/.test(v) == false) {
                    this.rules.push('This field must contain at least one number.')
                }
                if (this.validateSymbol && /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(v) == false) {
                    this.rules.push('This field must contain at least one special character.')
                }
                if (validateRules) {
                    event.target.setCustomValidity(
                        this.rules.length > 0 ? this.$t('validation.regex', { attribute: this.placeholder }) : ''
                    )
                }
                if (this.validateConfirm != null) {
                    event.target.setCustomValidity(
                        this.validateConfirm != event.target.value
                            ? this.$t('validation.confirmation', { attribute: this.placeholder })
                            : ''
                    )
                }
            },
            toggle(show) {
                this.visible =
                    this.validateStrength == false &&
                    this.validateCaps == false &&
                    !this.validateUppercase &&
                    !this.validateLowercase &&
                    !this.validateNumber &&
                    !this.validateSymbol
                        ? false
                        : show
            },
            isCapsOn(event) {
                if (this.validateCaps == true) {
                    this.capsOn =
                        event && typeof event.getModifierState === 'function' && event.getModifierState('CapsLock')
                }
            }
        }
    }
</script>
